import React from 'react'
import dynamic from 'next/dynamic'
import PropTypes, { elementType } from 'prop-types'
import { DefaultSeo } from 'next-seo'
import Head from 'next/head'
import Script from 'next/script'
import '../styles/app.scss'
// Global CSS (Pages Router only allows global stylesheet imports here).

import 'react-pdf/dist/Page/AnnotationLayer.css'

import 'react-pdf/dist/Page/TextLayer.css'
import 'src/components/shared/PdfViewer/pdfViewer.scss'
import SEO from 'src/common/constants/nextSEO'
import { AnalyticsProvider } from 'src/common/analytics'
import { TrustMarkersProvider } from 'src/common/trust-markers'
import { FeatureFlagProvider } from 'src/common/ab-testing/context'
import { LeadIdProvider } from 'src/common/lead-id'
import { ChatbotProvider } from 'src/common/ChatbotProvider'
import { useRouter } from 'next/router'
import { parseServerCookies } from 'src/common/serverUtils'
import { ultramarine } from 'src/components/shared/styles'
import getConfig from 'src/common/runtimeConfig'
import {
  getVisitId,
  VisitIdServerAdapter,
} from '@achieve/cx-event-horizon/browser'
import { getAppTrustMarkers } from 'src/common/getAppTrustMarkers'
import { FooterTrustMarkersProvider } from 'src/common/footerTrustMarkersContext'
import ErrorPage from './_error.page'
import NotFoundPage from './404.page'

const ChatbotFloating = dynamic(
  () =>
    import('src/components/shared/Chatbot/AiChatFloating').then(
      (mod) => mod.ChatbotFloating
    ),
  { ssr: false }
)

const defaultSeoWithoutCanonical = Object.fromEntries(
  Object.entries(SEO).filter(([key]) => key !== 'canonical')
)

export const getDefaultSeoProps = (Component) =>
  Component === ErrorPage || Component === NotFoundPage
    ? defaultSeoWithoutCanonical
    : SEO

const PznTracker = dynamic(
  () =>
    import('../src/common/analytics/Pzn-tacker').then((mod) => mod.PznTracker),
  { ssr: false }
)
function App({
  Component,
  pageProps: {
    csrfToken,
    recaptchaKey,
    dashboardAuthHost,
    dashboardRedirectHost,
    cookie,
    visitId,
    leadId,
    trustMarkers,
    footerTrustMarkersCombination,
    ...pageProps
  },
}) {
  const analyticsProps = {
    event_origin: 'client',
    page_name: 'FDR Home',
    ...pageProps,
  }

  const { query } = useRouter() //for testing
  const nonce = process.env.nonce
  const hideForTest = query?.hasOwnProperty('performance')
    ? query.performance
    : null //for testing should be removed

  const isCrawler = pageProps.isCrawler || false
  const { publicRuntimeConfig } = getConfig()
  const defaultSeoProps = getDefaultSeoProps(Component)

  return (
    <div className={`${ultramarine.variable} font-ultramarine`}>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <DefaultSeo {...defaultSeoProps} />
      <Script id="env-init" nonce={nonce}>
        {`window.env=${JSON.stringify({
          csrfToken,
          recaptchaKey,
          dashboardAuthHost,
          dashboardRedirectHost,
        })}`}
      </Script>
      <LeadIdProvider leadIdFromServerCookies={cookie?.leadId}>
        <FeatureFlagProvider>
          <AnalyticsProvider
            isCrawler={isCrawler}
            pageProps={analyticsProps}
            urlParams={hideForTest}
          >
            <PznTracker />
            <TrustMarkersProvider trustMarkers={trustMarkers}>
              <FooterTrustMarkersProvider
                footerTrustMarkersCombination={footerTrustMarkersCombination}
              >
                <div id="modals" className="z-200 absolute" />
                <ChatbotProvider>
                  <Component {...pageProps} />
                  <ChatbotFloating />
                </ChatbotProvider>
              </FooterTrustMarkersProvider>
            </TrustMarkersProvider>
          </AnalyticsProvider>
        </FeatureFlagProvider>
      </LeadIdProvider>
      {/*
      // Disable temporarily insider integration: https://billsdev.atlassian.net/browse/PXP-6298
      <Script
        async
        nonce={nonce}
        strategy="afterInteractive"
        id="insider-profile"
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `window.insiderFlow = 1;
window.InsiderQueue = window.InsiderQueue || [];
window.InsiderQueue.push({
  type: "user",
  value: {
    uuid: "${leadId}",
    language: "en_US",
    gdpr_optin: true
  }
});
window.InsiderQueue.push({
  type: "attribute",
  value: {
    visit_id: "${visitId || 'N/A'}",
    profile_origin: "${publicRuntimeConfig.APP_BASE_PATH}"
  }
});
window.InsiderQueue.push({ type: "init" });`,
        }}
      />
      <Script
        async
        id="insider-vendor"
        strategy="afterInteractive"
        crossorigin="anonymous"
        nonce={nonce}
        src={publicRuntimeConfig.INSIDER_SRC}
        type="text/javascript"
      />
      */}
      {recaptchaKey ? (
        <Script
          id="recaptcha"
          src={`https://www.google.com/recaptcha/api.js?render=${recaptchaKey}`}
          nonce={nonce}
          defer={true}
        />
      ) : null}
    </div>
  )
}

App.getInitialProps = async ({ ctx }) => {
  const cookie = parseServerCookies(ctx)
  let visitId
  if (ctx?.req && ctx?.res)
    visitId = getVisitId(new VisitIdServerAdapter(ctx.req, ctx.res))
  const leadId = cookie?.leadId
  const preview = ctx.query?.preview === 'true'

  const { trustMarkers, footerTrustMarkersCombination } =
    await getAppTrustMarkers(preview)

  return {
    pageProps: {
      cookie,
      visitId,
      leadId,
      trustMarkers,
      footerTrustMarkersCombination,
    },
  }
}

App.defaultProps = {
  pageProps: {},
  router: {},
}
App.propTypes = {
  Component: elementType.isRequired,
  pageProps: PropTypes.object,
  router: PropTypes.object,
}

export default App
